import { tngaTaggingObjName } from './config/common-config';
import { Store } from '../store';
import { IEventManagerStoreTO } from './hook/use-event-manager';
import { TngaEventManager } from './tnga-event-manager';
import { IInsurance, ITaggingEventNodes } from 'common-services/event-config';
import { appRoot, setAppRoot } from 'index';
import { setShadowContainer } from 'services/event-manager';

export type GenericStoreType = Store | null | IEventManagerStoreTO;

export interface IEvents {
  name: string;
  data?: Record<string, Record<string, boolean | number | string | undefined> | IInsurance | string> | null;
  tagging?: boolean;
}

export class CommonEventManager {
  public appSelector: string | null = null;

  public appSelector$: HTMLElement | null = null;

  public store: GenericStoreType = null;

  public businessEvents: Record<string, any> = {}; // eslint-disable-line @typescript-eslint/no-explicit-any

  public taggingEvents: Record<string, Record<string, ITaggingEventNodes>> = {};

  public features: FeatureNames | null;

  private inBody = false;

  private tngaService: TngaEventManager;

  constructor(
    appSelector: string,
    store: GenericStoreType,
    businessEvents: Record<string, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
    taggingEvents: Record<string, Record<string, ITaggingEventNodes>>,
    features?: FeatureNames
  ) {
    this.appSelector$ = appSelector !== '' ? document?.querySelector(appSelector) || null : null;
    this.tngaService = new TngaEventManager(appSelector, store, taggingEvents);
    this.store = store as Store;
    this.businessEvents = businessEvents;
    this.taggingEvents = taggingEvents;
    this.features = features || null;
  }

  setStoreContext = (context: GenericStoreType, appSelector: string): void => {
    this.store = context as Store;
    this.tngaService = new TngaEventManager(appSelector, context, this.taggingEvents);
  };

  publishTaggingEvents = (events: Array<IEvents>, appSelector = this.appSelector$): boolean => {
    const tngaDataLayer = window[tngaTaggingObjName];
    if (tngaDataLayer) {
      this.tngaService.publishTaggingEvents(events, appSelector);
    }
    return true;
  };

  publishBusinessEvent = (events: Array<IEvents>, appSelector = this.appSelector$): boolean => {
    events.forEach((obj: TimApp.IAppBusinessEvents) => {
      if (obj.name) {
        const eventName = this.businessEvents[obj.name] || null;
        const fqs = 'fqs';
        if (obj.name === 'offerChosen' && window[fqs] && this.store?.ELEMENT_TYPE === 'NEW_CARS') {
          window[fqs].set({
            insurance: obj.data ? obj.data.detail.quoteId : null,
          });
        }
        if (eventName && appSelector) {
          const event: CustomEvent = obj.data ? new CustomEvent(eventName, obj.data) : new CustomEvent(eventName);
          return appSelector.dispatchEvent(event);
        }
        return true;
      }
      return true;
    });
    return true;
  };

  subscribe(appSelector = this.appSelector$): boolean {
    this.onCloseListened = this.onCloseListened.bind(this);
    this.onConfirmOfferListened = this.onConfirmOfferListened.bind(this);
    this.onCloseAfterTaggingListened = this.onCloseAfterTaggingListened.bind(this);

    if (appSelector !== null) {
      // subscribe to close widget event
      appSelector.addEventListener(this.businessEvents.closeWidget, this.onCloseListened, false);
      //appSelector.addEventListener(this.businessEvents.widgetClosed, this.onCloseListened, false);
      if (this.businessEvents.closeWidgetNCXButton) {
        appSelector.addEventListener(this.businessEvents.closeWidgetNCXButton, this.onCloseListened, false);
      }
      // subscribe to close widget after tagging event
      appSelector.addEventListener(
        this.businessEvents.closeWidgetAfterTagging,
        this.onCloseAfterTaggingListened,
        false
      );
      // subscribe to confirm offer event
      appSelector.addEventListener(this.businessEvents.offerChosen, this.onConfirmOfferListened, false);
      return true;
    }
    return false;
  }

  unsubscribe(appSelector = this.appSelector$): boolean {
    if (appSelector !== null) {
      appSelector.removeEventListener(this.businessEvents.closeWidget, this.onCloseListened, false);
      if (this.businessEvents.closeWidgetNCXButton) {
        appSelector.removeEventListener(this.businessEvents.closeWidgetNCXButton, this.onCloseListened, false);
      }
      appSelector.removeEventListener(
        this.businessEvents.closeWidgetAfterTagging,
        this.onCloseAfterTaggingListened,
        false
      );
      // subscribe to confirm offer event
      appSelector.removeEventListener(this.businessEvents.offerChosen, this.onConfirmOfferListened, false);
      return true;
    }
    return false;
  }

  onConfirmOfferListened(): boolean {
    if (!this.inBody) {
      this.publishBusinessEvent([{ name: 'closeWidget', data: { detail: { internal: true } } }]);
    }
    return true;
  }

  onCloseListened(e: CustomEvent, appSelector = this.appSelector$): boolean {
    const isInternal = e.detail && e.detail.internal;

    // x sign click - do tagging first and send 'close widget after tagging' event to close widget in index.tsx business events handling
    if (!isInternal) {
      this.publishTaggingEvents([{ name: 'widgetClosed', tagging: true }]);
      this.publishBusinessEvent([{ name: 'closeWidgetAfterTagging' }]);
    }
    if (isInternal) {
      return this.closeWidget();
    }
    this.unsubscribe();
    return true;
  }

  onCloseAfterTaggingListened(e: CustomEvent, appSelector = this.appSelector$): boolean {
    return this.closeWidget(appSelector);
  }

  closeWidget(appSelector = this.appSelector$): boolean {
    // clean-up done in container's componentWillUnmount

    if (appRoot) {
      appRoot.unmount();
      setAppRoot(null);
      setShadowContainer(null);
      this.unsubscribe();
      this.publishBusinessEvent([{ name: 'widgetClosed', data: null }]);
      return true;
    }
    return false;
  }

  init(): boolean {
    this.unsubscribe();
    this.subscribe();
    return true;
  }
}
